import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { dispatchApi, downloadAuthed } from '../api/services';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { ArrowLeft, Download, Send, Loader2, FileText, CheckCircle } from 'lucide-react';

export default function DispatchBatchDetail() {
  const { id } = useParams();
  const [batch, setBatch] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [marking, setMarking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dispatchApi.get(id);
      setBatch(res.data.batch);
      setShipments(res.data.shipments || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load');
    } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function generate() {
    setGenerating(true);
    try {
      const res = await dispatchApi.generateXlsx(id);
      toast.success(`Generated ${res.data.filename}`);
      // Auto-trigger the download via the authenticated helper. A plain
      // window.open would drop the Bearer token and 401.
      await downloadAuthed(`/dispatch/${id}/download-xlsx`, res.data.filename)
        .catch((err) => toast.error(err.response?.data?.error || 'Download failed'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'XLSX generation failed');
    } finally { setGenerating(false); }
  }

  async function downloadExisting() {
    try {
      await downloadAuthed(`/dispatch/${id}/download-xlsx`, batch?.xlsx_filename || 'dispatch.xlsx');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Download failed');
    }
  }

  // Vendor-facing variant — same data, but without the Order ID column.
  // The dispatch team emails this one to the vendor; they keep the with-
  // Order-ID copy internally to re-merge into the vendor's return file
  // before tracking upload (which matches on Order ID).
  async function downloadNoOrderId() {
    try {
      const base = (batch?.xlsx_filename || 'dispatch.xlsx').replace(/\.xlsx$/i, '');
      await downloadAuthed(`/dispatch/${id}/download-xlsx-no-order-id`, `${base}-NOID.xlsx`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Download failed');
    }
  }

  async function markEmailed() {
    setMarking(true);
    try {
      await dispatchApi.markEmailed(id);
      toast.success('Marked as emailed to vendor');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setMarking(false); }
  }

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading…</div>;
  if (!batch) return <div className="p-6 text-sm text-gray-500">Batch not found.</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <Link to="/dispatch" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mb-2">
        <ArrowLeft className="w-3.5 h-3.5" /> Dispatch batches
      </Link>

      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{batch.batch_number}</h1>
            <div className="text-sm text-gray-600 mt-1">
              Status: <strong className="capitalize">{batch.status}</strong> ·
              {batch.is_reship_file
                ? <span className="ml-1 text-purple-700">-RESHIP file</span>
                : <span className="ml-1">standard file</span>}
              {batch.dispatch_date && <> · dispatch date {batch.dispatch_date}</>}
              {batch.needs_revised_file && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  Products changed — regenerate &amp; re-send revised file
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {/* Generate visible only when batch is still pending. Download +
                Mark-as-emailed show once we're past pending — xlsx_filename
                may be null (batch generated pre-fix and we cleared stale
                pointers), but the download endpoint regenerates on demand
                from the batch's shipments. Emailed_at state still gates
                Mark-as-emailed correctly. */}
            {(batch.status === 'pending' || batch.needs_revised_file) && (
              <button onClick={generate} disabled={generating}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                {batch.status === 'pending' ? 'Generate XLSX' : 'Regenerate revised XLSX'}
              </button>
            )}
            {batch.status !== 'pending' && (
              <>
                <button onClick={downloadNoOrderId}
                        title="Vendor-facing copy — no Order ID column"
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                  <Download className="w-4 h-4" /> Download for vendor
                </button>
                <button onClick={downloadExisting}
                        title="Internal copy — includes Order ID for tracking-upload matching"
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
                  <Download className="w-4 h-4" /> Download (internal)
                </button>
              </>
            )}
            {batch.status !== 'pending' && (!batch.emailed_to_vendor_at || batch.needs_revised_file) && (
              <button onClick={markEmailed} disabled={marking}
                      className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">
                {marking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {batch.emailed_to_vendor_at ? 'Mark revised file as emailed' : 'Mark as emailed'}
              </button>
            )}
          </div>
        </div>

        <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><dt className="text-[10px] uppercase text-gray-500">Shipments</dt><dd>{shipments.length}</dd></div>
          <div><dt className="text-[10px] uppercase text-gray-500">XLSX generated</dt>
            <dd>{batch.xlsx_generated_at ? format(new Date(batch.xlsx_generated_at), 'dd MMM HH:mm') : '—'}</dd></div>
          <div><dt className="text-[10px] uppercase text-gray-500">Emailed to vendor</dt>
            <dd className="flex items-center gap-1">
              {batch.emailed_to_vendor_at
                ? (<><CheckCircle className="w-3 h-3 text-green-600" />{format(new Date(batch.emailed_to_vendor_at), 'dd MMM HH:mm')}</>)
                : '—'}
            </dd></div>
          <div><dt className="text-[10px] uppercase text-gray-500">File</dt>
            <dd className="font-mono text-xs">{batch.xlsx_filename || '(not yet)'}</dd></div>
        </dl>
      </div>

      {/* Orders in this batch — grouped from per-item shipments per Kalpana
          UAT round 3 (B3). One row per order; products + tracking are
          aggregated. Pre-fix had one row per item which made multi-product
          orders look like multiple separate orders. */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200 font-medium text-sm">
          Orders in this batch
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs font-semibold uppercase text-gray-500">
            <tr>
              <th className="text-left px-4 py-2">Order</th>
              <th className="text-left px-4 py-2">Customer</th>
              <th className="text-left px-4 py-2">Products</th>
              <th className="text-right px-4 py-2">Total qty</th>
              <th className="text-left px-4 py-2">Tracking</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              // Group shipments by order_id. Each group becomes one table row.
              const byOrder = new Map();
              for (const s of shipments) {
                if (!byOrder.has(s.order_id)) {
                  byOrder.set(s.order_id, {
                    order_id: s.order_id,
                    order_id_external: s.order_id_external,
                    customer_name: s.customer_name,
                    items: [],
                  });
                }
                byOrder.get(s.order_id).items.push(s);
              }
              return Array.from(byOrder.values()).map((g) => {
                const totalQty = g.items.reduce((a, x) => a + (x.quantity || 0), 0);
                const tnSet = [...new Set(g.items.map((x) => x.tracking_number).filter(Boolean))];
                let tnDisplay;
                if (tnSet.length === 0) tnDisplay = '—';
                else if (tnSet.length === 1) tnDisplay = tnSet[0];
                else tnDisplay = `${tnSet.length} TNs`;
                return (
                  <tr key={g.order_id} className="border-t border-gray-100 align-top">
                    <td className="px-4 py-2 font-mono text-blue-600">
                      <Link to={`/orders/${g.order_id}`}>{g.order_id_external}</Link>
                    </td>
                    <td className="px-4 py-2">{g.customer_name}</td>
                    <td className="px-4 py-2 text-xs">
                      {g.items.map((it, i) => (
                        <div key={i}>
                          {it.product_name}
                          <span className="text-gray-400"> · qty {it.quantity}</span>
                        </div>
                      ))}
                    </td>
                    <td className="px-4 py-2 text-right font-semibold">{totalQty}</td>
                    <td className="px-4 py-2 font-mono text-xs"
                        title={tnSet.length > 1 ? tnSet.join('\n') : ''}>
                      {tnDisplay}
                    </td>
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
}
